import { motion, useInView } from 'framer-motion';
import { Briefcase, GraduationCap } from 'lucide-react';
import { useRef } from 'react';

// Data
const education = [
  {
    period: '2016 - 2019',
    institution: 'SMAN 6 Bekasi',
    company: 'IPA',
    description: 'Graduated from SMAN 6 Bekasi focus on science.',
  },
  {
    period: '2019 - 2024',
    institution: 'Universitas Dian Nuswantoro',
    company: 'Informatics Engineering',
    description: (
      <>
        <p>Bachelor Degree in Informatics Engineering from Universitas Dian Nuswantoro.</p>
        <p className="mt-2 font-semibold text-blue-400">GPA: 3.35/4.00</p>
      </>
    ),
  },
  {
    period: '2025 - Present',
    institution: 'Udemy',
    company: 'Web Developer Bootcamp (MERN)',
    description: (
      <>
        <p>70+ hours program covering responsive web development with HTML5, CSS3 (Flexbox, responsive design), modern JavaScript (ES6+), React.js, Bootstrap, Semantic UI, 
          and back-end RESTful APIs using Node.js, Express.js, and MongoDB, including authentication, authorization, security, Git, NPM, Unix CLI, and cloud deployment.</p>
      </>
    ),
  },
  {
    period: '2025 - Present',
    institution: 'Sanbercode',
    company: 'QA Engineer Bootcamp',
    description: (
      <>
        <p>4-week intensive QA training focused on manual and automation testing with real-world projects, covering QA fundamentals, SDLC, STLC, test plans, test cases, bug 
          reports, API testing (Postman), automation (Cypress), and Agile testing methods.</p>
      </>
    ),
  },
];

const workExperience = [
  {
    period: 'Mar 2022 - Jun 2022',
    position: 'Fullstack Developer',
    company: 'CV Rajawali Merah',
    description:
      'Built a responsive e-commerce platform using PHP, MySQL, and JavaScript with an admin dashboard for inventory and customer management, applying Git for version control and Chrome DevTools for debugging and performance optimization.',
  },
  {
    period: 'Dec 2023 - Mar 2024',
    position: 'Part Time Barista',
    company: 'Palette Coffee & Space',
    description:
      'Handled beverage preparation (coffee and non-coffee), managed inventory and monthly stock reports, recorded daily cashier transactions accurately, maintained café cleanliness, and delivered friendly, professional customer service.',
  },
  {
    period: 'Oct 2025 - Present',
    position: 'DevOps Engineer',
    company: 'PT PLN Icon Plus',
    description:
      'Probation as a DevOps Engineer at PT PLN Icon Plus, focus on learning Linux command system and its operating system itself.',
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
      delayChildren: 0.1
    }
  }
};

const boxVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

// Timeline item
const TimelineItem = ({ period, title, subtitle, description, isLast }) => (
  <div className={`relative pl-12 ${!isLast ? 'pb-12' : 'pb-4'}`}>
    {/* Timeline dot */}
    <div className="absolute left-0 z-10 w-4 h-4 bg-blue-500 border-2 border-blue-400 rounded-full shadow-lg top-2">
      <div className="absolute bg-blue-300 rounded-full inset-1 animate-pulse" />
    </div>
    
    {/* Timeline line */}
    {!isLast && (
      <div className="absolute left-2 top-6 w-0.5 h-full bg-gradient-to-b from-blue-500/50 to-gray-600/30" />
    )}
    
    <div className="pl-2">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="p-4 border rounded-lg bg-gray-800/30 border-gray-700/50 hover:bg-gray-800/50"
      >
        <span className="inline-block px-3 py-1 mb-3 text-sm text-blue-400 border rounded-full bg-blue-600/20 border-blue-500/30">
          {period}
        </span>
        <h3 className="mb-1 text-xl font-semibold text-white">{title}</h3>
        {subtitle && (
          <p className="mb-3 font-medium text-purple-400">{subtitle}</p>
        )}
        <div className="leading-relaxed text-gray-300">{description}</div>
      </motion.div>
    </div>
  </div>
);

// Timeline container
const Timeline = ({ data, type }) => (
  <div className="relative ml-2">
    {data.map((item, idx) => (
      <TimelineItem
        key={idx}
        period={item.period}
        title={type === 'edu' ? item.institution : item.position}
        subtitle={type === 'edu' ? item.company : item.company}
        description={item.description}
        isLast={idx === data.length - 1}
      />
    ))}
  </div>
);

// Main component
const Experiences = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.2 });

  return (
    <section
      id="experiences"
      className="relative min-h-screen px-6 py-20 overflow-hidden text-white bg-gradient-to-tr from-gray-900 via-slate-900 to-gray-900"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute bg-blue-500 rounded-full top-20 left-20 w-72 h-72 blur-3xl" />
        <div className="absolute bg-purple-500 rounded-full bottom-20 right-20 w-96 h-96 blur-3xl" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.02]" />

      <div className="relative z-10 mx-auto max-w-7xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 className="text-4xl font-bold text-transparent md:text-5xl bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text">
            Experiences
          </h2>
        </motion.div>

        <motion.div
          ref={containerRef}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid items-start gap-12 lg:grid-cols-2"
        >
          {/* Education */}
          <motion.div
            variants={boxVariants}
            whileHover={{ scale: 1.02, y: -5 }}
            transition={{ type: 'spring', stiffness: 300 }}
            className="p-8 border shadow-2xl bg-gray-800/50 backdrop-blur-sm border-gray-700/50 rounded-2xl"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 rounded-lg bg-blue-600/20">
                <GraduationCap className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-blue-400">Educations & Courses</h3>
            </div>
            <Timeline data={education} type="edu" />
          </motion.div>

          {/* Work Experience */}
          <motion.div
            variants={boxVariants}
            whileHover={{ scale: 1.02, y: -5 }}
            transition={{ type: 'spring', stiffness: 300 }}
            className="p-8 border shadow-2xl bg-gray-800/50 backdrop-blur-sm border-gray-700/50 rounded-2xl"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 rounded-lg bg-purple-600/20">
                <Briefcase className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold text-purple-400">Work Experiences</h3>
            </div>
            <Timeline data={workExperience} type="work" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experiences;